// Decode the dubbing timeline's filmstrip WHILE the source video uploads.
//
// Creating a project spends its first stretch pushing the file to S3, and the
// timeline then spends another stretch decoding the very same bytes back out of
// it. Both read the same local File, so they can run at the same time: this
// starts decoding the strip the moment the upload starts, holds the frames in
// memory, and writes them into the strip cache (the exact payload
// useVideoThumbnails saves and reads) as soon as the S3 url — the cache key —
// is known. By the time the project opens, the strip is partly or fully there.
//
// The prewarm runs TO COMPLETION, whatever the timeline does. It used to be
// stopped the moment the timeline mounted, which meant a long video's strip
// was only ever complete if the user then sat through the timeline's own
// decode — leave early and the next visit pulled the whole file back down from
// S3. The timeline now adopts a running job instead (adoptTimelinePrewarm):
// it seeds its strip from the frames in hand, streams the rest as they decode,
// and leaves the front-to-back grind here — reading the file on disk one chunk
// at a time, which no network pipeline can match. stopTimelinePrewarm remains
// for the one case the timeline cannot adopt (the job died before its sample
// table was read) and must own the extraction alone.

import { v4 as uuidv4 } from 'uuid';
import { createByteStore } from '@src/services/byteStore';
import { planChunks, stripSeconds } from '@src/services/chunkPlan';
import { MAX_MEDIA_FILE_BYTES } from '@src/services/mediaFile';
import {
  canUseWebCodecs,
  decodeChunk,
  dropLocalSource,
  normalizeCacheKey,
  readSampleTable,
  registerLocalSource,
  sampleIndexAt,
  stampOf,
  writeCache,
} from '@src/services/mediaExtractor';

// MUST match useVideoThumbnails' STRIP_SECONDS and THUMB_HEIGHT: the payload
// written here is read back by its loadCached, and a different grid spacing or
// frame height would hand the lane a strip it doesn't expect.
const STRIP_SECONDS = 1;
const THUMB_HEIGHT = 120;

// How often the frames held so far are flushed to the cache once the url is
// known — same cadence as the timeline's own saves.
const SAVE_INTERVAL_MS = 4000;

// The decode is paced in batches: this many chunks flat out, then a breather.
// Unpaced, the decoder threads run saturated for the whole file — minutes on a
// long video — right under the upload and whatever the user is doing in the
// create popup. The breather is when the machine gets to breathe AND when the
// cache write happens, so a tab closed mid-upload keeps everything up to the
// last batch rather than the last timer tick.
const BATCH_CHUNKS = 4;
const BATCH_BREATHER_MS = 250;

// Jobs that know their url, so the timeline can find and stop them.
const jobs = new Map();

/**
 * The strip-cache payload for what `job` holds. Shape mirrors
 * useVideoThumbnails' save() exactly.
 */
const payloadOf = (job) => {
  const seconds = [...job.bySecond.keys()].sort((a, b) => a - b);
  return {
    aspect: job.meta.aspect,
    videoDuration: job.meta.videoDuration,
    gridSec: STRIP_SECONDS,
    complete:
      job.grid.length > 0 && job.grid.every((sec) => job.bySecond.has(sec)),
    times: seconds,
    blobs: seconds.map((second) => job.bySecond.get(second)),
  };
};

// Resolves once the write is committed — the final flush is awaited so a
// reader told "the prewarm has stopped" cannot beat its frames to the cache.
// Intermediate flushes just let it run.
const saveJob = (job) => {
  if (!job.url || !job.bySecond.size) return Promise.resolve();
  return writeCache(job.url, 'strip', payloadOf(job));
};

/**
 * decodeChunk hands a frame shared by several grid seconds to only one of
 * them. Those seconds resolve to the SAME source sample, so the held blob is
 * exactly their frame too — copy it rather than leaving them missing forever.
 *
 * Only the missing seconds are worth resolving, and their sharer is always a
 * grid NEIGHBOUR: second → resolved sample is monotonic, so seconds on one
 * frame form a contiguous run. Walking the run beats stamping the whole grid —
 * one binary search per 5400 seconds of a 90-minute source, for the usual
 * handful actually missing.
 */
const copySharedFrames = (job, table) => {
  const { track, samples, offsetSec } = table;
  const stampAt = (second) =>
    stampOf(
      samples[sampleIndexAt(samples, second, track.timescale, offsetSec)],
      track.timescale,
    );
  // The held second whose frame is `stamp`, walking from `start` in `step`
  // direction until the equal-stamp run ends. At most one second per stamp
  // ever received the frame, so the first held one found is the only one.
  const holderFrom = (start, step, stamp) => {
    for (let i = start; i >= 0 && i < job.grid.length; i += step) {
      if (stampAt(job.grid[i]) !== stamp) return null;
      if (job.bySecond.has(job.grid[i])) return job.grid[i];
    }
    return null;
  };
  job.grid.forEach((second, at) => {
    if (job.bySecond.has(second)) return;
    const stamp = stampAt(second);
    const holder =
      holderFrom(at - 1, -1, stamp) ?? holderFrom(at + 1, 1, stamp);
    if (holder !== null) job.hold(second, job.bySecond.get(holder));
  });
};

/**
 * Start decoding `file`'s filmstrip now, before its S3 url exists. Returns a
 * handle, or null when the browser can't (no WebCodecs) or shouldn't (file too
 * large to be a timeline source anyway):
 *
 * - `attachUrl(url)`: the upload finished — from here on frames are saved under
 *   `url`'s strip-cache key, starting with everything already decoded.
 * - `cancel()`: the upload failed — stop and save nothing.
 */
export const startTimelinePrewarm = (file) => {
  if (!file || !canUseWebCodecs() || file.size > MAX_MEDIA_FILE_BYTES) {
    return null;
  }

  const job = {
    url: null,
    cancelled: false,
    controller: new AbortController(),
    bySecond: new Map(),
    grid: [],
    meta: { aspect: 16 / 9, videoDuration: 0 },
    // The most recent cache write. stopTimelinePrewarm awaits it on top of
    // `done`, covering the write attachUrl fires when decoding already
    // finished before the upload did.
    lastSave: Promise.resolve(),
    // An adopted timeline hears about each frame the moment it decodes.
    listeners: new Set(),
  };
  // Settled once the sample table has been read — the earliest moment an
  // adopter can size its strip. Rejects if the job dies before then, which
  // tells the adopter to run its own extraction instead.
  job.ready = new Promise((resolve, reject) => {
    job.settleReady = resolve;
    job.failReady = reject;
  });
  // Nobody may ever adopt this job; its rejection must not surface as
  // unhandled. Adopters get the original promise and their own rejection.
  job.ready.catch(() => {});

  // Every frame goes through here, wherever it was found, so an adopter never
  // misses one.
  const hold = (second, blob) => {
    job.bySecond.set(second, blob);
    job.listeners.forEach((listener) => listener(second, blob));
  };
  // On the job too, for copySharedFrames — which only gets handed the job.
  job.hold = hold;

  // The sample table comes off the shared range reader, which speaks urls; a
  // placeholder registered as a local source keeps the read local. Dropped in
  // run()'s finally.
  const tempUrl = `https://prewarm.local/${uuidv4()}`;
  registerLocalSource(tempUrl, file);

  const run = async () => {
    try {
      const table = await readSampleTable(tempUrl, job.controller.signal);
      job.meta = { aspect: table.aspect, videoDuration: table.videoDuration };
      job.grid = stripSeconds(table.videoDuration, STRIP_SECONDS);
      // The strip's shape is known — an adopter can size itself from here.
      job.settleReady({ ...job.meta, gridSec: STRIP_SECONDS });
      const chunks = planChunks(
        table.keyframes,
        table.totalSize,
        table.track.timescale,
        table.offsetSec,
      );

      // Front to back, one chunk's bytes in memory at a time. A moment is
      // offered to every chunk up to the one that owns its frame: it always
      // resolves to a sample BEFORE the next keyframe (a keyframe at or before
      // the moment would itself be the resolved sample), so `toSec` bounds the
      // moments a chunk can answer. That also skips reading chunks with nothing
      // to answer — on a faststart file the head chunk is the whole moov.
      const missing = new Set(job.grid);
      let lastSave = 0;
      let batched = 0;
      for (
        let at = 0;
        at < chunks.length && missing.size && !job.cancelled;
        at += 1
      ) {
        const chunk = chunks[at];
        const seconds = [...missing].filter((sec) => sec < chunk.toSec);
        if (seconds.length) {
          // eslint-disable-next-line no-await-in-loop
          const buffer = await file.slice(chunk.start, chunk.end).arrayBuffer();
          const store = createByteStore();
          store.add(chunk.start, new Uint8Array(buffer));
          // eslint-disable-next-line no-await-in-loop
          await decodeChunk({
            table,
            store,
            chunk,
            seconds,
            height: THUMB_HEIGHT,
            signal: job.controller.signal,
            onFrame: (second, blob) => {
              if (missing.delete(second)) hold(second, blob);
            },
          });
          batched += 1;
          if (batched >= BATCH_CHUNKS) {
            batched = 0;
            const now = performance.now();
            if (now - lastSave >= SAVE_INTERVAL_MS) {
              lastSave = now;
              saveJob(job);
            }
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) =>
              setTimeout(resolve, BATCH_BREATHER_MS),
            );
          }
        }
      }
      if (!job.cancelled && missing.size) copySharedFrames(job, table);
    } catch (error) {
      // A prewarm is only ever a head start. Whatever stopped it — an aborted
      // decode, a file with no video track — the timeline pipeline still owns
      // the real extraction and will produce the strip on its own.
    } finally {
      // A no-op on a job that read its table (ready already resolved); on one
      // that died before that, it unblocks an adopter waiting to size itself,
      // telling it to extract on its own.
      job.failReady(new Error('prewarm ended before its sample table'));
      dropLocalSource(tempUrl);
      // Flush whatever is in hand, complete or not: the timeline reads the cache
      // before doing anything else, so every frame saved here is one it never
      // has to decode again. Awaited, because `done` settling is what tells
      // stopTimelinePrewarm the cache is safe to read.
      job.lastSave = saveJob(job);
      await job.lastSave;
      if (job.url) jobs.delete(normalizeCacheKey(job.url));
    }
  };
  job.done = run();

  return {
    attachUrl: (url) => {
      if (!url || job.cancelled) return;
      job.url = url;
      jobs.set(normalizeCacheKey(url), job);
      job.lastSave = saveJob(job);
    },
    cancel: () => {
      job.cancelled = true;
      job.controller.abort();
    },
  };
};

/**
 * Adopt the prewarm running for `url`, or null when none is (it finished, died,
 * or never existed — the strip cache holds whatever it managed either way).
 *
 * The job keeps running exactly as it was; adoption is a window onto it:
 *
 * - `ready`: resolves `{ aspect, videoDuration, gridSec }` once the sample
 *   table is read — rejects if the job dies first, which means extract alone.
 * - `frames`: the live second → blob Map of everything decoded so far. Read it
 *   AFTER subscribing, so a frame landing in between is seen by one or both.
 * - `onFrame(listener)`: hear every new frame; returns the unsubscribe.
 * - `done`: settles when the job ends, its final cache write included. Whether
 *   the grid actually filled is the adopter's own frames to judge.
 */
export const adoptTimelinePrewarm = (url) => {
  const job = jobs.get(normalizeCacheKey(url));
  if (!job || job.cancelled) return null;
  return {
    ready: job.ready,
    frames: job.bySecond,
    onFrame: (listener) => {
      job.listeners.add(listener);
      return () => job.listeners.delete(listener);
    },
    done: job.done.then(() => job.lastSave),
  };
};

/**
 * Stop the prewarm running for `url`, if any, and resolve once its frames are
 * flushed to the strip cache — for the one taker that cannot adopt (the job
 * died before its sample table), so the timeline extracts as sole owner.
 */
export const stopTimelinePrewarm = async (url) => {
  const key = normalizeCacheKey(url);
  const job = jobs.get(key);
  if (!job) return;
  job.cancelled = true;
  job.controller.abort();
  await job.done;
  await job.lastSave;
  // Also covers a job whose decoding finished before its url was attached —
  // run()'s own cleanup ran too early to know the key.
  jobs.delete(key);
};
